'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import useCustomRouter from '@/hooks/useCustomRouter';
import { useDebounce } from '@/hooks/useDebounce';
import useFormat from '@/hooks/useFormat';
import useAlgoliaProducts from '@/lib/hooks/useAlgoliaProducts';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { trackClick, getProductsIndexName, type AlgoliaProduct } from '@/lib/algolia';
import { rememberQueryId } from '@/lib/algolia/query-id-store';
import { MAX_SEARCH_QUERY_LENGTH, isSearchQueryTooLong } from '@/lib/algolia/query-limits';
import { SEARCH_AUTOCOMPLETE_DEBOUNCE_MS, isSearchQueryLongEnough } from '@/lib/algolia/search-tuning';
import { normalizeBarcode, isBarcodeShapedQuery, isBarcodeSearchEnabled } from '@/lib/algolia/barcode';
import { lookupBarcode } from '@/lib/algolia/barcode-lookup';
import { useAlgoliaKey } from '@/providers/algolia-key';
import toast from '@/components/atoms/toaster/helpers/toast';
import { classnames } from '@/utils/classnames/classnames';
import type { Currency } from '@/types/currency';
import type { Locale } from '@/project.config';

interface SearchBarProps {
  className?: string;
  inputClassName?: string;
}

// EWM-Algolia v1 — autocomplete dropdown. Queries the locale-mapped
// products index via `useAlgoliaProducts` once the shopper has typed at least
// SEARCH_AUTOCOMPLETE_MIN_CHARS characters and paused for
// SEARCH_AUTOCOMPLETE_DEBOUNCE_MS (EWM-833 — see lib/algolia/search-tuning.ts
// for both values and why). Hits click through to the PDP via `/p/<slug>`;
// submit (or "View all results") navigates to `/search?query=…` for the full
// results page at ANY length — the gate is on the live dropdown only.
//
// v1 ships read-only: no Insights tracking, no click attribution. v2 wires
// `search-insights` per the integration plan.
const SearchBar = ({ className = '', inputClassName = '' }: SearchBarProps) => {
  const translate = useTranslations();
  const router = useCustomRouter();
  const { formatCurrency } = useFormat();
  const inputId = useId();
  const params = useParams();
  const locale = (params?.locale as Locale | undefined) ?? 'en-AU';
  const { securedApiKey } = useAlgoliaKey();

  const [value, setValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce<string>(value, SEARCH_AUTOCOMPLETE_DEBOUNCE_MS);
  // EWM-382: don't fire the autocomplete query for an overlong term — it would
  // 400 against Algolia just like the full-page search did.
  const queryTooLong = isSearchQueryTooLong(value);
  // EWM-833: nor for one or two characters. Gated on the DEBOUNCED value, not
  // the live one, so deleting from 3 chars to 2 stops the next request rather
  // than racing an in-flight one.
  const queryLongEnough = isSearchQueryLongEnough(debouncedQuery);
  // Scope autocomplete to the dealer's product selection so the dropdown
  // doesn't surface products that resolve to 404 at the PDP (the BFF route
  // guard also enforces selection-id, so a hit that's outside the dealer's
  // catalog would 404 on click).
  const { selectedStore } = useStoreAndBusinessUnits();
  const { hits, nbHits, queryID, indexName, isLoading } = useAlgoliaProducts({
    query: debouncedQuery,
    hitsPerPage: 5,
    productSelectionKeys: selectedStore?.productSelectionKeys,
    // Surface queryID so hit clicks fire Algolia Insights `clickedObjectIDsAfterSearch`.
    clickAnalytics: true,
    // EWM-833 — the single fetch gate. `enabled: false` nulls the SWR key, so no
    // request is issued at all (previously this passed `query: ''`, which relied
    // on the hook's own empty-query short-circuit — the explicit flag says what
    // it means and can't be defeated by a future caller passing a categoryKey).
    enabled: !queryTooLong && queryLongEnough,
  });

  // Close the dropdown on outside click. Headless-UI alternatives buy us
  // focus management we don't need for a single input.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    // EWM-382 / EWM-469: block the navigation for an overlong query and
    // surface a friendly error. Without this the header search bar pushed
    // `/search?query=<overlong>`, which commercetools rejected (its 256-char
    // search-expression limit) and 404'd on the SSR getPage.
    // EWM-382 rework: per design, the header bar shows this as a top-right
    // toast (not an inline message). The /search page input keeps its own
    // inline validation error (search-page-header).
    if (isSearchQueryTooLong(trimmed)) {
      toast.error(translate('search.query-too-long', { max: MAX_SEARCH_QUERY_LENGTH }), { position: 'top-right' });
      setIsOpen(false);
      return;
    }
    setIsOpen(false);

    // EWM-927 — barcode search. Only pure-digit (8–14) queries take this path,
    // so product-name / keyword / SKU (alphanumeric) search is unchanged. Inert
    // until NEXT_PUBLIC_ENABLE_BARCODE_SEARCH is on and the index carries barcodes.
    if (isBarcodeSearchEnabled()) {
      const normalized = normalizeBarcode(trimmed);
      if (isBarcodeShapedQuery(normalized)) {
        try {
          const { nbHits, hit } = await lookupBarcode({
            securedApiKey,
            indexName: getProductsIndexName(locale),
            normalizedBarcode: normalized,
            productSelectionKeys: selectedStore?.productSelectionKeys,
          });
          if (nbHits === 1 && hit?.slug && hit?.sku) {
            router.push(`/${hit.slug}/p/${hit.sku}`);
            return;
          }
          router.push(`/search?barcode=${encodeURIComponent(normalized)}`);
          return;
        } catch {
          // Fail-open: a lookup error must never trap the shopper — fall through
          // to the standard text-search results page.
        }
      }
    }

    router.push(`/search?query=${encodeURIComponent(trimmed)}`);
  };

  // Hide the dropdown while the query is invalid (overlong) or too short to have
  // been sent (EWM-833) — with no request in flight there is nothing to show,
  // and rendering the "no results" state for 1–2 characters would read as a real
  // empty result rather than "still waiting for you to type".
  const showDropdown = isOpen && !queryTooLong && queryLongEnough;

  return (
    <div ref={containerRef} className={classnames('relative', className)}>
      <form role="search" onSubmit={handleSubmit} className="relative flex w-full items-center">
        <MagnifyingGlassIcon className="absolute left-4 h-5 w-5 text-gray-400" aria-hidden />
        <label htmlFor={inputId} className="sr-only">
          {translate('common.search')}
        </label>
        <input
          id={inputId}
          type="search"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={translate('common.search-placeholder')}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={`${inputId}-listbox`}
          className={classnames(
            'text-16 w-full rounded-md border-0 bg-white py-2 pr-4 pl-12 text-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-white/50 focus:outline-none',
            inputClassName,
          )}
        />
      </form>

      {showDropdown && (
        <div
          id={`${inputId}-listbox`}
          role="listbox"
          aria-label={translate('common.search-suggestions')}
          className="absolute top-full right-0 left-0 z-50 mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {isLoading && hits.length === 0 && (
            <p className="text-13 px-4 py-3 text-gray-500" role="status">
              {translate('common.search-loading')}
            </p>
          )}
          {!isLoading && hits.length === 0 && (
            <p className="text-13 px-4 py-3 text-gray-500" role="status">
              {translate('common.search-no-results')}
            </p>
          )}
          {hits.length > 0 && (
            <ul className="max-h-[420px] divide-y divide-gray-100 overflow-y-auto">
              {hits.map((hit, index) => (
                <li key={hit.objectID} role="option" aria-selected="false">
                  <SearchHit
                    hit={hit}
                    onSelect={() => {
                      setIsOpen(false);
                      // Fire-and-forget Algolia Insights click event.
                      // queryID may be undefined for the brief window
                      // before the first response — skip then.
                      if (queryID) {
                        trackClick({
                          index: indexName,
                          queryID,
                          objectIDs: [hit.objectID],
                          positions: [index + 1],
                          eventName: 'Hit clicked (autocomplete)',
                        });
                        // EWM-809 — remember the queryID for PDP conversion/purchase attribution.
                        rememberQueryId(hit.objectID, queryID, indexName);
                      }
                    }}
                    formatCurrency={formatCurrency}
                  />
                </li>
              ))}
            </ul>
          )}
          {hits.length > 0 && (
            <Link
              href={`/search?query=${encodeURIComponent(debouncedQuery.trim())}`}
              className="text-13 hover:text-primary block border-t border-gray-100 px-4 py-3 text-center font-semibold text-gray-700"
              onClick={() => setIsOpen(false)}
              underlineOnHover={false}
            >
              {translate('common.search-view-all', { count: nbHits })}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

interface SearchHitProps {
  hit: AlgoliaProduct;
  onSelect: () => void;
  formatCurrency: (value: number, currency: Currency) => string;
}

// Jaycar's media CDN base — used to upgrade bare filenames from the `sd*`
// indices (e.g. "SB2486_..._30214.jpg") to fully-qualified URLs. The retail
// indices already emit absolute URLs; the B2B indexing pipeline today emits
// either nothing or a bare filename, so we patch this on the FE for v1.
// Mirror of the pattern used in `utils/mappers/map-product.ts` for
// downloads (where `/resources/` is the path; `/images/` here).
const MEDIA_IMAGE_BASE = 'https://media.jaycar.com.au/product/images/';
const resolveImageUrl = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  return /^https?:\/\//.test(raw) ? raw : `${MEDIA_IMAGE_BASE}${raw}`;
};

const SearchHit = ({ hit, onSelect, formatCurrency }: SearchHitProps) => {
  const translate = useTranslations();
  // Algolia `images` on `sd*` records is sometimes `[[]]` (an empty nested
  // array — indexing-pipeline quirk) instead of `[]`, so flatten defensively
  // before reading `productListingImage`.
  const firstImageEntry = hit.images?.find(
    (entry): entry is { productListingImage?: { url?: string; altText?: string } } =>
      !!entry && typeof entry === 'object' && !Array.isArray(entry),
  );
  const imageUrl = resolveImageUrl(firstImageEntry?.productListingImage?.url);
  const imageAlt = firstImageEntry?.productListingImage?.altText ?? hit.name ?? '';
  const active = hit.priceGroup?.[0]?.activePrice;
  const price =
    typeof active?.centAmount === 'number'
      ? formatCurrency(active.centAmount / 100, (active.currencyCode ?? 'AUD') as Currency)
      : null;
  // PDP route pattern is `/{slug}/p/{sku}` (see backend `ProductRouter`).
  // Bare `/p/<slug>` doesn't resolve to any Frontastic page.
  const href = hit.slug && hit.sku ? `/${hit.slug}/p/${hit.sku}` : '#';

  return (
    <Link
      href={href}
      onClick={onSelect}
      // EWM-672: `block` opts out of the Link atom's default `w-fit`, so the row
      // fills the full dropdown width — the whole row (not just the product
      // name) is the click target and the hover highlight spans full width.
      block
      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50"
      underlineOnHover={false}
    >
      <div className="relative flex size-12 shrink-0 items-center justify-center bg-gray-50">
        {imageUrl ? (
          <Image src={imageUrl} alt={imageAlt} fill sizes="48px" className="object-contain mix-blend-multiply" />
        ) : (
          <PhotoIcon className="size-6 text-gray-400" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-14 line-clamp-1 font-semibold text-gray-800">{hit.name ?? '—'}</p>
        {hit.sku && (
          <p className="text-12 text-gray-500">
            {translate('common.cat-no')}: {hit.sku}
          </p>
        )}
      </div>
      {price && <p className="text-14 shrink-0 font-bold text-gray-900">{price}</p>}
    </Link>
  );
};

export default SearchBar;
